import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { t, pickMulti } from "../i18n/translations";
import { api, formatApiError } from "../lib/api";
import { ChevronLeft, ChevronRight, Check, ArrowUpRight, MapPin } from "lucide-react";
import { toast } from "sonner";

function pad(n) { return n.toString().padStart(2, "0"); }
function fmtDate(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

const LOCATIONS = [
  { key: "sarmenstorf", name: "Sarmenstorf", address: "Obere Feldeggstr. 13, 5614 Sarmenstorf" },
  { key: "rupperswil", name: "Rupperswil", address: "Aarestrasse 20, 5102 Rupperswil" },
];

function MonthCalendar({ value, onChange, lang }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const initialView = value ? new Date(value) : today;
  const [view, setView] = useState(new Date(initialView.getFullYear(), initialView.getMonth(), 1));

  const firstDay = new Date(view.getFullYear(), view.getMonth(), 1);
  const lastDay = new Date(view.getFullYear(), view.getMonth() + 1, 0);
  const startWeekday = (firstDay.getDay() + 6) % 7; // monday=0
  const days = [];
  for (let i = 0; i < startWeekday; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(view.getFullYear(), view.getMonth(), d));

  const monthName = view.toLocaleDateString(lang, { month: "long", year: "numeric" });
  const dayShort = ["mon","tue","wed","thu","fri","sat","sun"];

  return (
    <div className="border border-border rounded-md p-5 bg-white" data-testid="booking-calendar">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} className="p-2 hover:bg-muted rounded" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" strokeWidth={1.5}/></button>
        <span className="font-display text-lg capitalize">{monthName}</span>
        <button onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} className="p-2 hover:bg-muted rounded" data-testid="cal-next"><ChevronRight className="w-4 h-4" strokeWidth={1.5}/></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-xs text-ink-muted text-center mb-1">
        {dayShort.map((d) => <div key={d}>{t(lang, `days_short.${d}`)}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const past = d < today;
          const sel = value === fmtDate(d);
          return (
            <button
              key={i}
              disabled={past}
              onClick={() => onChange(fmtDate(d))}
              data-testid={`cal-day-${fmtDate(d)}`}
              className={`aspect-square text-sm rounded-md transition-colors ${past ? "text-ink-muted/30 cursor-not-allowed" : sel ? "bg-terracotta text-white" : "hover:bg-muted text-ink"}`}
            >{d.getDate()}</button>
          );
        })}
      </div>
    </div>
  );
}

export default function Booking() {
  const { lang } = useLang();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1);
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [location, setLocation] = useState("sarmenstorf");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", birthdate: "", email: "", phone: "", address: "", postal_code: "", city: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [params] = useSearchParams();
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data || []);
      const preselect = params.get("service");
      if (preselect && r.data?.some((s) => s.id === preselect)) {
        setServiceId(preselect);
        setStep(2);
      }
    });
  }, [params]);

  useEffect(() => {
    if (!date || !serviceId) { setSlots([]); return; }
    setLoadingSlots(true);
    api.get(`/bookings/availability?date=${date}&service_id=${serviceId}`).then((r) => setSlots(r.data.slots || [])).finally(() => setLoadingSlots(false));
  }, [date, serviceId]);

  const selectedService = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        service_id: serviceId,
        customer_name: `${form.first_name} ${form.last_name}`.trim(),
        customer_email: form.email,
        customer_phone: form.phone,
        customer_birthdate: form.birthdate,
        customer_address: form.address,
        customer_postal_code: form.postal_code,
        customer_city: form.city,
        date, time, location, notes: form.notes, language: lang,
      });
      setSuccess(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-32 text-center" data-testid="booking-success">
        <div className="w-16 h-16 rounded-full bg-sage/15 text-sage flex items-center justify-center mx-auto mb-6"><Check className="w-7 h-7" strokeWidth={1.5}/></div>
        <h1 className="font-display text-4xl text-ink">{t(lang, "booking.success_title")}</h1>
        <p className="mt-6 text-ink-muted leading-relaxed">{t(lang, "booking.success_text")}</p>
        <div className="mt-8 inline-block text-left bg-sand-soft border border-border rounded-md p-6">
          <div className="text-sm"><span className="text-ink-muted">{t(lang, "admin.service")}:</span> <span className="text-ink ml-2">{pickMulti(selectedService?.name, lang)}</span></div>
          <div className="text-sm mt-2"><span className="text-ink-muted">{t(lang, "admin.date")}:</span> <span className="text-ink ml-2">{date} – {time}</span></div>
        </div>
        <div className="mt-10 flex justify-center gap-4">
          <Link to="/" className="px-6 py-3 border border-border rounded-full">← Home</Link>
          <button onClick={() => { setSuccess(false); setStep(1); setServiceId(""); setDate(""); setTime(""); setForm({first_name:"",last_name:"",email:"",phone:"",notes:""}); }} className="px-6 py-3 bg-terracotta text-white rounded-full" data-testid="new-booking-btn">{t(lang, "booking.new_booking")}</button>
        </div>
      </div>
    );
  }

  const stepLabels = ["step1", "step2", "step3", "step4"];

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-12 py-10 sm:py-16" data-testid="booking-page">
      <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">— {t(lang, "nav.book")}</div>
      <h1 className="font-display text-4xl sm:text-5xl text-ink leading-tight max-w-2xl">{t(lang, "booking.title")}</h1>
      <p className="mt-4 text-ink-muted max-w-2xl">{t(lang, "booking.subtitle")}</p>

      {/* Stepper */}
      <div className="mt-8 sm:mt-12 flex items-center gap-2 overflow-x-auto pb-2">
        {[1, 2, 3, 4].map((n) => (
          <React.Fragment key={n}>
            <div className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-[10px] sm:text-xs uppercase tracking-wider whitespace-nowrap ${step >= n ? "bg-ink text-sand" : "bg-muted text-ink-muted"}`} data-testid={`stepper-${n}`}>
              <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] ${step > n ? "bg-sage text-white" : "bg-white/15"}`}>{step > n ? <Check className="w-3 h-3" strokeWidth={2}/> : n}</span>
              <span className="hidden sm:inline">{t(lang, `booking.${stepLabels[n-1]}`)}</span>
              <span className="sm:hidden">{n}</span>
            </div>
            {n < 4 && <div className="flex-1 h-px bg-border min-w-[10px]" />}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-8 sm:mt-10 grid lg:grid-cols-3 gap-6 lg:gap-8">
        <div className="lg:col-span-2">
          {/* Permanent location selector – visible on ALL steps */}
          <div className="mb-6 p-4 sm:p-5 bg-sand-soft border-2 border-terracotta/30 rounded-lg" data-testid="booking-location-banner">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-terracotta" strokeWidth={2} />
              <h3 className="font-display text-lg text-ink">Standort</h3>
              <span className="text-xs text-ink-muted ml-auto">jederzeit änderbar</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="booking-location-selector">
              {LOCATIONS.map((loc) => (
                <button
                  key={loc.key}
                  type="button"
                  onClick={() => setLocation(loc.key)}
                  data-testid={`booking-location-${loc.key}`}
                  className={`p-3 rounded-md border-2 text-left transition-all ${location === loc.key ? "border-terracotta bg-white shadow-sm" : "border-border bg-white/60 hover:border-ink/30"}`}
                >
                  <div className="flex items-start gap-2.5">
                    <MapPin className={`w-4 h-4 mt-0.5 shrink-0 ${location === loc.key ? "text-terracotta" : "text-ink-muted"}`} strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className={`text-sm font-medium ${location === loc.key ? "text-terracotta" : "text-ink"}`}>{loc.name}</div>
                        {location === loc.key && <Check className="w-3.5 h-3.5 text-terracotta" strokeWidth={2.5} />}
                      </div>
                      <div className="text-[11px] text-ink-muted mt-0.5 leading-snug">{loc.address}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {step === 1 && (
            <div data-testid="booking-step-1">
              <h2 className="font-display text-2xl mb-6">{t(lang, "booking.choose_service")}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((svc) => (
                  <button key={svc.id} onClick={() => setServiceId(svc.id)} className={`text-left p-5 rounded-md border transition-all ${serviceId === svc.id ? "border-terracotta bg-terracotta/5" : "border-border hover:border-ink/40"}`} data-testid={`booking-service-${svc.slug}`}>
                    <div className="aspect-[16/9] mb-4 rounded overflow-hidden bg-muted"><img src={svc.image_url} alt="" className="w-full h-full object-cover"/></div>
                    <div className="font-display text-lg text-ink leading-tight">{pickMulti(svc.name, lang)}</div>
                    <div className="mt-2 text-xs text-ink-muted">{svc.duration_minutes} {t(lang, "services.minutes")} · CHF {svc.price_chf}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 2 && (
            <div data-testid="booking-step-2">
              <h2 className="font-display text-2xl mb-6">{t(lang, "booking.choose_date")}</h2>
              <MonthCalendar value={date} onChange={setDate} lang={lang} />
            </div>
          )}
          {step === 3 && (
            <div data-testid="booking-step-3">
              <h2 className="font-display text-2xl mb-6">{t(lang, "booking.choose_time")}</h2>
              {loadingSlots ? (
                <div className="text-ink-muted">…</div>
              ) : slots.length === 0 ? (
                <div className="p-8 text-center border border-border rounded-md bg-muted/40">
                  <div className="text-ink-muted">{t(lang, "booking.no_slots")}</div>
                  <button
                    type="button"
                    onClick={() => setShowWaitlist(true)}
                    className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-full hover:bg-sage-hover transition-colors"
                    data-testid="join-waitlist-btn"
                  >
                    {t(lang, "booking.join_waitlist")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5">
                  {slots.map((s) => (
                    <button key={s} onClick={() => setTime(s)} className={`px-3 py-3 rounded-md border text-sm transition-colors ${time === s ? "bg-terracotta text-white border-terracotta" : "border-border bg-white hover:border-ink/30"}`} data-testid={`slot-${s}`}>{s}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          {step === 4 && (
            <div data-testid="booking-step-4">
              <h2 className="font-display text-2xl mb-6">{t(lang, "booking.step4")}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t(lang, "booking.first_name")} value={form.first_name} onChange={(v) => setForm({...form, first_name: v})} testid="input-firstname" required />
                <Field label={t(lang, "booking.last_name")} value={form.last_name} onChange={(v) => setForm({...form, last_name: v})} testid="input-lastname" required />
                <Field label={t(lang, "booking.birthdate")} type="date" value={form.birthdate} onChange={(v) => setForm({...form, birthdate: v})} testid="input-birthdate" />
                <Field label={t(lang, "booking.phone")} value={form.phone} onChange={(v) => setForm({...form, phone: v})} testid="input-phone" required />
                <div className="sm:col-span-2">
                  <Field label={t(lang, "booking.email")} type="email" value={form.email} onChange={(v) => setForm({...form, email: v})} testid="input-email" required />
                </div>
                <div className="sm:col-span-2">
                  <Field label={t(lang, "booking.address")} value={form.address} onChange={(v) => setForm({...form, address: v})} testid="input-address" />
                </div>
                <Field label={t(lang, "booking.postal_code")} value={form.postal_code} onChange={(v) => setForm({...form, postal_code: v})} testid="input-postal" />
                <Field label={t(lang, "booking.city")} value={form.city} onChange={(v) => setForm({...form, city: v})} testid="input-city" />
                <div className="sm:col-span-2">
                  <Field label={t(lang, "booking.notes")} value={form.notes} onChange={(v) => setForm({...form, notes: v})} textarea testid="input-notes"/>
                </div>
              </div>
            </div>
          )}

          {/* Selbstzahler-Hinweis – auf Schritt 4 vor dem Absenden */}
          {step === 4 && (
            <div className="mt-8 p-4 bg-terracotta/5 border-l-4 border-terracotta rounded-md" data-testid="booking-insurance-notice">
              <div className="flex items-start gap-3">
                <span className="text-terracotta text-lg leading-none mt-0.5">ⓘ</span>
                <div className="text-sm text-ink leading-relaxed">
                  <strong>Hinweis zur Bezahlung:</strong> Die Behandlungen sind <strong>nicht von den Schweizer Krankenkassen anerkannt</strong>.
                  Eine Kostenrückerstattung über die Grund- oder Zusatzversicherung ist nicht möglich.
                  Die Kosten sind selbst zu tragen. Mit dem Absenden bestätigst du, dass du unsere{" "}
                  <a href="/agb" target="_blank" rel="noreferrer" className="text-terracotta underline hover:text-terracotta-hover">AGB</a>{" "}
                  und die <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-terracotta underline hover:text-terracotta-hover">Datenschutzerklärung</a> gelesen hast und akzeptierst.
                </div>
              </div>
            </div>
          )}

          {/* Nav buttons */}
          <div className="mt-10 flex justify-between gap-3">
            <button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="px-6 py-3 border border-border rounded-full disabled:opacity-30"
              data-testid="booking-back"
            >{t(lang, "booking.back")}</button>
            {step < 4 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={(step === 1 && !serviceId) || (step === 2 && !date) || (step === 3 && !time)}
                className="px-7 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover disabled:opacity-30"
                data-testid="booking-next"
              >{t(lang, "booking.next")}</button>
            ) : (
              <button
                onClick={submit}
                disabled={!form.first_name || !form.last_name || !form.email || !form.phone || submitting}
                className="px-7 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover disabled:opacity-30 inline-flex items-center gap-2"
                data-testid="booking-submit"
              >{t(lang, "booking.submit")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5}/></button>
            )}
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-1 lg:order-last order-first">
          <div className="bg-sand-soft border border-border rounded-md p-5 sm:p-6 lg:sticky lg:top-28" data-testid="booking-summary">
            <div className="text-xs uppercase tracking-widest text-terracotta mb-4">{t(lang, "booking.summary")}</div>
            <SummaryRow label={t(lang, "admin.service")} value={pickMulti(selectedService?.name, lang) || "—"} />
            <button
              type="button"
              onClick={() => setStep(1)}
              data-testid="summary-location-change"
              className="w-full flex justify-between items-baseline py-1 text-left group"
              title="Standort ändern"
            >
              <span className="text-sm text-ink-muted group-hover:text-ink">Standort</span>
              <span className="text-sm text-ink group-hover:text-terracotta">
                {LOCATIONS.find((l) => l.key === location)?.name || "—"}
                <span className="ml-1 text-xs text-terracotta">✎</span>
              </span>
            </button>
            <SummaryRow label={t(lang, "admin.date")} value={date || "—"} />
            <SummaryRow label={t(lang, "admin.time")} value={time || "—"} />
            {selectedService && (
              <div className="mt-5 pt-5 border-t border-border flex justify-between items-baseline">
                <span className="text-sm text-ink-muted">{t(lang, "admin.total")}</span>
                <span className="font-display text-2xl text-ink">CHF {selectedService.price_chf}</span>
              </div>
            )}
            {/* Always-visible action button inside summary */}
            <div className="mt-5 pt-5 border-t border-border">
              {step < 4 ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={(step === 1 && !serviceId) || (step === 2 && !date) || (step === 3 && !time)}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover disabled:opacity-30 transition-colors"
                  data-testid="booking-next-summary"
                >
                  {t(lang, "booking.next")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={!form.first_name || !form.last_name || !form.email || !form.phone || submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover disabled:opacity-30 transition-colors"
                  data-testid="booking-submit-summary"
                >
                  {submitting ? "…" : t(lang, "booking.submit")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
              {step > 1 && (
                <button
                  onClick={() => setStep((s) => Math.max(1, s - 1))}
                  className="mt-2 w-full px-6 py-2.5 text-sm text-ink-muted hover:text-ink transition-colors"
                  data-testid="booking-back-summary"
                >
                  ← {t(lang, "booking.back")}
                </button>
              )}
            </div>
          </div>
        </aside>
      </div>

      {showWaitlist && (
        <WaitlistModal
          onClose={() => setShowWaitlist(false)}
          onDone={() => { setShowWaitlist(false); setWaitlistDone(true); }}
          serviceId={serviceId}
          serviceName={pickMulti(selectedService?.name, lang)}
          date={date}
          lang={lang}
        />
      )}
      {waitlistDone && (
        <div className="fixed bottom-6 right-6 z-50 bg-sage text-white px-5 py-3 rounded-md shadow-lg text-sm animate-fade-up" data-testid="waitlist-toast">
          {t(lang, "booking.waitlist_success")}
        </div>
      )}
    </div>
  );
}

function WaitlistModal({ onClose, onDone, serviceId, serviceName, date, lang }) {
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", preferred_time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.customer_phone) return;
    setSubmitting(true);
    try {
      await api.post("/waitlist", {
        service_id: serviceId,
        ...form,
        preferred_date: date,
        language: lang,
      });
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4 overflow-auto" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="waitlist-modal">
      <form onSubmit={submit} className="bg-white rounded-md max-w-md w-full p-8">
        <div className="text-xs uppercase tracking-[0.25em] text-sage mb-3">{t(lang, "booking.waitlist_title")}</div>
        <h2 className="font-display text-3xl text-ink">{t(lang, "booking.waitlist_subtitle")}</h2>
        <p className="text-sm text-ink-muted mt-2">{t(lang, "booking.waitlist_intro")}</p>
        <div className="mt-5 p-3 bg-sand-soft border border-border rounded-md text-xs text-ink-muted">
          <div><span className="uppercase tracking-widest">Behandlung</span> · {serviceName}</div>
          <div><span className="uppercase tracking-widest">Wunschdatum</span> · {date}</div>
        </div>
        <div className="mt-5 space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-muted">Name *</span>
            <input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1 w-full px-3 py-2 border border-border rounded-md" data-testid="wl-name" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-muted">E-Mail *</span>
              <input required type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1 w-full px-3 py-2 border border-border rounded-md" data-testid="wl-email" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-muted">Telefon *</span>
              <input required value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="mt-1 w-full px-3 py-2 border border-border rounded-md" data-testid="wl-phone" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-muted">Wunsch-Uhrzeit (optional)</span>
            <input type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="mt-1 w-full px-3 py-2 border border-border rounded-md" data-testid="wl-time" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-muted">Notiz (optional)</span>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 border border-border rounded-md" data-testid="wl-notes" />
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border rounded-md text-sm">Abbrechen</button>
          <button type="submit" disabled={submitting} className="px-6 py-2.5 bg-sage text-white rounded-md text-sm disabled:opacity-30" data-testid="wl-submit">
            {submitting ? "…" : t(lang, "booking.waitlist_submit")}
          </button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", textarea = false, testid, required = false }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-ink-muted">{label}{required && " *"}</span>
    {textarea ? (
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="mt-2 w-full px-4 py-2.5 border border-border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-terracotta/40" />
    ) : (
      <input data-testid={testid} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full px-4 py-2.5 border border-border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-terracotta/40" />
    )}
  </label>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between py-2 text-sm">
    <span className="text-ink-muted">{label}</span>
    <span className="text-ink text-right max-w-[60%]">{value}</span>
  </div>
);
